import React from 'react';
import { LayoutProvider, useLayout } from '../../contexts/layout-context';
import { AppSidebar } from './app-sidebar';
import { AppNavbar, NavbarTab } from './app-navbar';

interface AppLayoutContentProps {
  children: React.ReactNode;
  activePage?: string;
  flushContent?: boolean;
  navbarProps?: {
    showBackButton?: boolean;
    backPath?: string;
    tabs?: NavbarTab[];
    activeTab?: string;
    onTabChange?: (tabId: string) => void;
    children?: React.ReactNode;
  };
}

const AppLayoutContent: React.FC<AppLayoutContentProps> = ({
  children,
  activePage,
  flushContent = false,
  navbarProps,
}) => {
  const { isMobile, sidebarCollapsed } = useLayout();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <AppSidebar activePage={activePage} />

      {/* Navbar */}
      <AppNavbar {...navbarProps} />

      {/* Main content */}
      <main
        className={`pt-16 transition-all duration-300 ${
          isMobile ? 'ml-0' : sidebarCollapsed ? 'ml-16' : 'ml-80'
        }`}
      >
        <div className={flushContent ? 'overflow-hidden p-0' : 'p-4 md:p-6 lg:p-8'}>
          {children}
        </div>
      </main>
    </div>
  );
};

interface AppLayoutProps {
  children: React.ReactNode;
  activePage?: string;
  flushContent?: boolean;
  navbarProps?: {
    showBackButton?: boolean;
    backPath?: string;
    tabs?: NavbarTab[];
    activeTab?: string;
    onTabChange?: (tabId: string) => void;
    children?: React.ReactNode;
  };
  layoutProviderProps?: {
    defaultSidebarOpen?: boolean;
    defaultSidebarCollapsed?: boolean;
  };
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  activePage,
  flushContent = false,
  navbarProps,
  layoutProviderProps,
}) => {
  return (
    <LayoutProvider {...layoutProviderProps}>
      <AppLayoutContent
        activePage={activePage}
        flushContent={flushContent}
        navbarProps={navbarProps}
      >
        {children}
      </AppLayoutContent>
    </LayoutProvider>
  );
};